import { QueryHookOptions } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { KeywordFilteringGq } from '../../../../../graphql/projects/models/KeywordFilteringGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import {
  CommandGq,
  ResponseGq,
  VariablesGq,
} from '../../../../../graphql/projects/queries/useGetKeywordsGq';
import { useDebounceValue } from '../../../../../logic/otherUtilities/useDebounceValue';
import { KeywordTableState } from './useKeywordTableState';
const deepEqual = require('deep-equal');

export function useGetDebouncedKeywords(
  keywordTableState: KeywordTableState,
  options?: QueryHookOptions<ResponseGq, VariablesGq>,
  debounceDelay: number = 300,
  domainIds: number[] = []
) {
  // Debounce filter
  const debouncedFilter = useDebounceValue<KeywordFilteringGq>(
    keywordTableState.backendFilterState,
    debounceDelay
  );

  // Create command
  // paging and sorting does not need to be debounced
  // it "feels" much better if these are instant
  const finalCommand = useMemo(
    (): CommandGq => ({
      domainIds,
      keywordQuery: {
        filtering: debouncedFilter,
        paging: keywordTableState.pagingState,
        sorting: keywordTableState.sortingState,
        //selecting: debouncedSelection,
        excludeSelectedFilter: true,
      },
    }),
    [debouncedFilter, keywordTableState.sortingState, keywordTableState.pagingState, domainIds]
  );
  // We need to memoize options
  // Options can't be memoized with reference equality - so we need to utilize deep equality
  // Otherwise the "debounce" step will perceive the options as new in every single render, causing an infinite loop
  // Alternatively we could skip debouncing options
  // ... but then they don't vary together with the command which causes problems in case you enter something like "skip"
  const [memoizedOptions, setMemoizedOptions] = useState(options);
  useEffect(() => {
    if (deepEqual(options, memoizedOptions)) {
      return;
    }
    setMemoizedOptions(options);
  }, [options]);

  const debouncedOptions = useDebounceValue(memoizedOptions, debounceDelay);

  // It's important that we use "skip" instantly. It can't be debounced. Also skip if we can't find a project ID right now
  const skip =
    !!options?.skip ||
    !!memoizedOptions?.skip ||
    !!debouncedOptions?.skip ||
    !finalCommand?.keywordQuery?.filtering?.projectId;

  const finalOptions = {
    ...debouncedOptions,
    skip,
    pollIntervall: skip ? undefined : debouncedOptions?.pollInterval,
  };

  const result = projectsApiService.queries.useGetKeywords(finalCommand, finalOptions);

  // Workaround because there's a bug in apollo
  // https://github.com/apollographql/apollo-client/issues/7552
  useEffect(() => {
    if (skip || !debouncedOptions?.pollInterval) {
      result.query.stopPolling();
      return;
    }

    if (!skip && !!debouncedOptions?.pollInterval) {
      result.query.startPolling(debouncedOptions?.pollInterval);
    }
  }, [skip, debouncedOptions?.pollInterval]);

  return result;
}
